import {
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type AnyFrontendStage,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { Inline } from '@atlaskit/primitives/inline';

import { Stack } from '@atlaskit/primitives/stack';

import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import { usePlacementApi } from '@post-office/use-placement-api';

import { useCallback, useEffect, useState } from 'react';

const placementConfig: PlacementBrowserConfig = {
	placementId: `rovo-chat-sources`, // Change this to you placement id. This needs to match the folder name.
};

const placement = {
	createFrontend: createPlacementFrontend(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: AnyFrontendStage;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	NoProps
> = createFrontend((Messages) => () => {
	const apiResponse = usePlacementApi<{
		messages: Array<EligibleMessage>;
	}>('/');

	// Track dismissed message IDs for optimistic dismissals
	const [dismissedMessageIds, setDismissedMessageIds] = useState<Set<string>>(new Set());

	// Copy messages into state to ensure React sees changes even if array reference is the same
	// This pattern matches rovo-chat-button placement exactly
	const [messages, setMessages] = useState<Array<EligibleMessage>>([]);

	useEffect(() => {
		const { data, isLoading } = apiResponse;

		if (isLoading) {
			return;
		}

		if (data?.messages) {
			setMessages(data.messages);
		}
	}, [apiResponse]);

	const handleDismiss = useCallback((messageInstanceId: string) => {
		setDismissedMessageIds((prev) => new Set(prev).add(messageInstanceId));
	}, []);

	const messagesToShow = messages.filter(
		(message) => !dismissedMessageIds.has(message.messageInstanceId),
	);

	return (
		<Stack space="space.150">
			{messagesToShow.length > 0 && (
				<Stack as="ul" space="space.200">
					{messagesToShow.map((props) => (
						<Inline as="li" key={`${props.messageInstanceId}-${JSON.stringify(props.context)}`}>
							<Messages.render
								{...props}
								onDismiss={() => handleDismiss(props.messageInstanceId)}
							/>
						</Inline>
					))}
				</Stack>
			)}
		</Stack>
	);
});
